import React, { useState, useEffect, useRef } from "react";
import FeatureCard from "./FeatureCard";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import Pagination from "./Pagination";

const { FiInbox } = FiIcons;

const FeatureList = ({
  features,
  onUpvote,
  onUpdate,
  onDelete,
  onClose,
  onRelease,
  onReopen,
  onLoadInternalNote,
  onSaveInternalNote,
  isAdmin,
  emptyMessage = "No features yet",
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const featuresPerPage = 10;
  const listTopRef = useRef(null);

  // We deliberately do NOT reset currentPage just because the features
  // array changed — that includes releasing, closing, reopening, editing,
  // upvoting, or deleting an item, all of which can shrink/reorder the
  // current tab's filtered list. Resetting on every such change is what
  // used to bounce the admin back to page 1 after e.g. marking something
  // as Released, even though they wanted to keep working through the same
  // page/scroll position.
  //
  // A genuine navigation change (switching tabs, changing the priority
  // filter, or running a new search) is handled upstream: both App.jsx and
  // AdminPage.jsx key their <FeatureList> wrapper on
  // `${activeTab}-${selectedPriorities.join("-")}`, which remounts this
  // component from scratch on those changes — giving a fresh currentPage
  // of 1 naturally, without needing an effect here. (Search reorders/
  // filters the same underlying list without changing that key, which is
  // intentional — the currentPage set here is preserved across search 
  // input changes.)
  //
  // The only thing we still need to handle locally is the current page
  // becoming invalid — e.g. the last item on the final page was
  // deleted/closed/released out of the current filtered view — in which
  // case we clamp down to the new last page instead of showing a blank one.
  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(features.length / featuresPerPage));
    setCurrentPage((page) => Math.min(page, newTotalPages));
  }, [features.length]);

  const totalPages = Math.ceil(features.length / featuresPerPage);
  const indexOfFirst = (currentPage - 1) * featuresPerPage;
  const currentFeatures = features.slice(indexOfFirst, indexOfFirst + featuresPerPage);

  const goToPage = (page) => {
    setCurrentPage(page);
    // Scroll to the top of the list smoothly — not the whole page
    if (listTopRef.current) {
      listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (features.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.20)" }}
        >
          <SafeIcon icon={FiInbox} className="w-6 h-6 text-violet-400" />
        </div>
        <p className="text-gray-500 text-base">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Anchor for scroll-to-top on pagination — offset slightly so the list header stays visible */}
      <div ref={listTopRef} style={{ scrollMarginTop: "80px" }} />

      {/* Top pagination — mirrors the bottom one so long lists can be paged
          from either end without scrolling all the way down first */}
      {totalPages > 1 && (
        <div className="mb-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} />
        </div>
      )}

      <div className="space-y-3">
        {currentFeatures.map((feature) => (
          <FeatureCard
            key={feature.id}
            feature={feature}
            onUpvote={onUpvote}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onClose={onClose}
            onRelease={onRelease}
            onReopen={onReopen}
            onLoadInternalNote={onLoadInternalNote}
            onSaveInternalNote={onSaveInternalNote}
            isAdmin={isAdmin}
          />
        ))}
      </div>

      {/* Bottom pagination */}
      {totalPages > 1 && (
        <div className="mt-8">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} />
        </div>
      )}
    </div>
  );
};

export default FeatureList;
